import { Component, Event, Fragment, h, State, VNode } from '@stencil/core';
import { ProjectDoc } from 'common';
import { refreshSessionOrShowAlert } from 'src/global/service/interceptors';
import { User } from '../../global/models';
import { ProjectService } from '../services/ProjectService';
import { ProjectUtils } from '../utils/ProjectUtils';
import { CreateProjectModal } from './create-project-modal';
import { ConcourseProfile } from '../../components/concourse-profile/concourse-profile';

@Component({
  tag: 'concourse-projects',
  styleUrl: 'concourse-projects.css',
  //shadow: true,
})
export class ConcourseProjects {
  @State() currentIndex: number = 0;
  @State() projects: any[] = [];
  @State() newProjectModalOpen: boolean = false;
  @State() archiveModalOpen: boolean = false;
  @State() projectToArchive: ProjectDoc = null;
  @Event() showProfile;

  /* Convert the global property into a typed object */
  get user(): User {
    return globalThis.user;
  }

  get isOnline(): boolean {
    return navigator.onLine;
  }

  async componentWillLoad() {
    if (!this.user) {
      window.location.hash = '/login';
      return;
    }
    if (this.isOnline) {
      await refreshSessionOrShowAlert();
    }
    // sort but have 'demo' aways first
    this.projects = this.user.projectIds
      .filter(p => !!p)
      .sort((a, b) => {
        if (a === 'demo') return -1;
        if (b === 'demo') return 1;
        return parseInt(a) - parseInt(b);
      });
    this.loadProject();
  }

  /** Load the projects iteratively so there is a little more feedback */
  async loadProject() {
    const projectId = this.projects[this.currentIndex];
    console.log('Loading project', projectId);
    try {
      this.projects[this.currentIndex] = await ProjectService.getProjectDoc(`${projectId}`);
    } catch (error) {
      console.error('Error loading project', projectId, error);
      // we let flow continue because it will try load the next project
    }
    this.currentIndex++;
    if (this.currentIndex < this.projects.length) {
      this.loadProject();
    } else {
      console.log('All projects loaded');
    }
  }

  toggleNewProjectModal() {
    if (!this.user.doc.email_verified) {
      ConcourseProfile.present('Please verify your email before creating a project', () => {
        this.showCreateModal();
      });
      return;
    }
    this.showCreateModal();
  }

  async showCreateModal() {
    this.newProjectModalOpen = true;
    const projectId = await CreateProjectModal.present();
    if (projectId) {
      document.location.hash = `/project/${projectId}/dashboard`;
    }
    this.newProjectModalOpen = false;
  }

  showArchiveModal(event: Event, project: ProjectDoc) {
    event.preventDefault();
    event.stopPropagation();
    this.projectToArchive = project;
    this.archiveModalOpen = true;
  }

  handleArchiveModalDismiss() {
    this.archiveModalOpen = false;
    this.projectToArchive = null;
  }

  async handleArchiveConfirmed(event: CustomEvent<ProjectDoc>) {
    const project = event.detail;
    try {
      project.is_archived = !project.is_archived;
      await ProjectService.saveProjectDoc(project);
      // Trigger re-render by updating projects array
      this.projects = [...this.projects];
      this.handleArchiveModalDismiss();
    } catch (error) {
      console.error('Error archiving project', error);
      // Revert the change on error
      project.is_archived = !project.is_archived;
    }
  }

  groupProjectsByStatus() {
    const active: ProjectDoc[] = [];
    const archived: ProjectDoc[] = [];

    this.projects.forEach((project, index) => {
      const isProjectDoc = (project as ProjectDoc)._id !== undefined;
      if (!isProjectDoc) {
        // If not loaded yet, add to active by default
        active.push(project);
        return;
      }

      if (project.is_archived === true) {
        archived.push(project);
      } else {
        active.push(project);
      }
    });

    return { active, archived };
  }

  renderProjectCard(project: ProjectDoc | string, index: number): VNode {
    const projectDoc = typeof project !== 'string' ? project : null;
    const projectId = projectDoc?._id ?? (project as string);
    const desc = projectDoc ? projectDoc.description : index == this.currentIndex ? 'Loading...' : 'Pending';
    const thumbnailUrl = projectDoc ? ProjectUtils.getThumbnailUrl(projectDoc) : '/assets/img/card-media.png';
    const addressLine2 = projectDoc ? ProjectUtils.getAddressLine2(projectDoc) : '';
    const isAdmin = !!projectDoc && this.user.canAdministerProject(projectId);

    return (
      <ion-card class="project-card ion-no-margin" href={`/project/${projectId}/dashboard`}>
        <img width="400" height="225" src={thumbnailUrl} />
        <ion-card-header class="cc-padding">
          <div class="ion-display-flex ion-justify-content-between ion-align-items-center">
            <ion-text data-testid="project-name" class="typography-h6" color="light">
              {projectDoc?.name || `Project ${project}`}
            </ion-text>
            {isAdmin && projectId !== 'demo' && (
              <ion-button fill="clear" shape="round" color="light" onClick={e => this.showArchiveModal(e, projectDoc!)}>
                <ion-icon name="archive-outline" slot="icon-only"></ion-icon>
              </ion-button>
            )}
          </div>
          <ion-card-subtitle color="light" class="typography-body2-400 ion-no-margin">
            {projectDoc?.address || ''}
          </ion-card-subtitle>
          <ion-card-subtitle color="light" class="typography-body2-400 ion-no-margin">
            {addressLine2}
          </ion-card-subtitle>
        </ion-card-header>
        <ion-card-content>{desc}</ion-card-content>
      </ion-card>
    );
  }

  render() {
    const { active, archived } = this.groupProjectsByStatus();
    const sections = [];
    if (active.length > 0) sections.push('active');

    return (
      <Fragment>
        <concourse-header pageTitle="Projects" showHamburger={true} showLogo={true}>
          <ion-buttons>
            <ion-button color="primary" shape="round" fill="solid" id="create-project" class="ion-padding-end ion-hide-md-down" onClick={() => this.toggleNewProjectModal()}>
              <ion-icon name="add"></ion-icon>
              <ion-label>Create Project</ion-label>
            </ion-button>
          </ion-buttons>
        </concourse-header>
        <ion-content class="cards cc-no-line-accordion safe-area-bottom">
          <ion-accordion-group class multiple={true} value={sections}>
            {active.length > 0 && (
              <ion-accordion value="active" class="ion-padding-top">
                <ion-item slot="header">
                  <ion-label class="typography-body2-500">Active Projects ({active.length})</ion-label>
                </ion-item>
                <div slot="content">
                  <ion-grid class="ion-display-flex ion-flex-wrap" style={{ gap: '1rem' }}>
                    {active.map((project, index) => this.renderProjectCard(project, index))}
                  </ion-grid>
                </div>
              </ion-accordion>
            )}
            {archived.length > 0 && (
              <ion-accordion value="archived" class="ion-padding-top">
                <ion-item slot="header">
                  <ion-label class="typography-body2-500">Archived Projects ({archived.length})</ion-label>
                </ion-item>
                <div slot="content">
                  <ion-grid class="ion-display-flex ion-flex-wrap" style={{ gap: '1rem' }}>
                    {archived.map((project, index) => this.renderProjectCard(project, index))}
                  </ion-grid>
                </div>
              </ion-accordion>
            )}
          </ion-accordion-group>
        </ion-content>
        {this.archiveModalOpen && (
          <ion-modal cssClass="modal-height-auto concourse-modal" isOpen={this.archiveModalOpen} onDidDismiss={() => this.handleArchiveModalDismiss()} backdropDismiss={false}>
            <archive-project-modal project={this.projectToArchive} onConfirmed={e => this.handleArchiveConfirmed(e)} onDismiss={() => this.handleArchiveModalDismiss()} />
          </ion-modal>
        )}
      </Fragment>
    );
  }
}
